import { ArrayUtils, DateUtils } from '../../../utils'

/**
 * Formats raw media schedule
 * @param {Object} rawMediaSchedule
 * @param {String} rawMediaSchedule.end_time
 * @param {String|Number} rawMediaSchedule.end_week_day
 * @param {Number} rawMediaSchedule.id
 * @param {Number} rawMediaSchedule.media_id
 * @param {String} rawMediaSchedule.start_time
 * @param {String|Number} rawMediaSchedule.start_week_day
 */
function formatMediaSchedule({ end_time, end_week_day, id, media_id, start_time, start_week_day }) {
    return {
        end_time,
        end_week_day: parseInt(end_week_day, 10),
        id,
        media_id,
        start_time,
        start_week_day: parseInt(start_week_day, 10),
    }
}

/**
 * Format raw media to the desired item
 * @param {Object} rawMedia
 */
function formatMedia({ date_upload_endend, date_upload_started, created_at, date_end, date_start, schedules, ...media }) {
    return {
        ...media,
        date_upload_endend: date_upload_endend && DateUtils.UTCDateToTimestamp(date_upload_endend),
        date_upload_started: date_upload_started && DateUtils.UTCDateToTimestamp(date_upload_started),
        created_at: created_at && DateUtils.UTCDateToTimestamp(created_at),
        date_end: date_end && DateUtils.UTCDateToTimestamp(date_end),
        date_start: date_start && DateUtils.UTCDateToTimestamp(date_start),
        isWorking: false,
        schedules: schedules && schedules.map(formatMediaSchedule),
    }
}

/**
 * Get media array. Two places available: media list or selected playlist's media
 * @param {Object} state Current state
 * @param {Boolean} [inSelectedPlaylist=false] Tells if it will get the list from selected playlist
 */
function getMediaList(state, inSelectedPlaylist = false) {
    let mediaList

    // Select media list from last state (two places where we can get it, depending on the `inSelectedPlaylist` flag)
    if (inSelectedPlaylist) {
        // From selected playlist
        const { selected } = state
        const { playlists } = selected || {}
        const { media: listToSelect } = playlists
        mediaList = listToSelect
    } else {
        // From the media list
        mediaList = state.media
    }

    return mediaList
}

/**
 * Update media array. Two places available: media list or selected playlist's media
 * @param {Object} state Current state
 * @param {Boolean} [inSelectedPlaylist=false] Tells if it will update the list from selected playlist
 */
function updateMediaList(state, updatedList, inSelectedPlaylist = false) {
    let newState

    // Update the state (it needs to update the same spot where it got the list)
    if (inSelectedPlaylist) {
        // state -> selected -> playlists -> media
        newState = {
            ...state,
            selected: {
                ...state.selected,
                playlists: {
                    ...(state.selected || {}).playlists,
                    media: updatedList,
                },
            },
        }
    } else {
        // Media list
        newState = {
            ...state,
            media: updatedList,
        }
    }

    return newState
}

/**
 * Updates media data
 * @param {Object} state Current state
 * @param {Number} mediaID Media ID
 * @param {Object} newData Object with the new date to add or update
 * @param {Boolean} [inSelectedPlaylist=false] If true, it gets media from selected playlist.
 * If false, it gets media from media list
 * @returns {Object} New immutabled state
 */
function updateMedia(state, mediaID, newData, inSelectedPlaylist) {
    // Get media list
    const mediaList = getMediaList(state, inSelectedPlaylist)

    // If the mediaList was not defined, quit
    if (!mediaList) {
        return state
    }

    // Get index of current element in the list
    const index = mediaList.findIndex((m) => m.id === mediaID)
    if (index < 0) {
        return state
    }

    // Get current element
    const selectedMedia = mediaList[index]

    // Update the list
    const updatedList = ArrayUtils.insertItem(mediaList, index, {
        ...selectedMedia,
        ...newData,
    })

    // Updates state
    return updateMediaList(state, updatedList, inSelectedPlaylist)
}

/**
 * Updates the state with a failed count fetch
 * @param {Object} state lastState
 * @param {String} countKey Key of the count to update
 */
function setCountFailure(state, countKey) {
    return {
        ...state,
        counts: {
            ...state.counts,
            [countKey]: {
                ...state.counts[countKey],
                isWorking: false,
            },
        },
    }
}

/**
 * Updates the state with the beginning of a count fetch
 * @param {Object} state lastState
 * @param {String} countKey Key of the count to update
 */
function setCountRequest(state, countKey) {
    return {
        ...state,
        counts: {
            ...state.counts,
            [countKey]: {
                ...state.counts[countKey],
                isWorking: true,
            },
        },
    }
}

/**
 * Updates the state with the a successful count fetch
 * @param {Object} state lastState
 * @param {String} countKey Key of the count to update
 */
function setCountSuccess(state, countKey, count) {
    return {
        ...state,
        counts: {
            ...state.counts,
            [countKey]: {
                ...state.counts[countKey],
                isWorking: false,
                count,
            },
        },
    }
}

export { formatMedia, formatMediaSchedule, getMediaList, setCountFailure, setCountRequest, setCountSuccess, updateMedia }
